import React from "react";
import { styled } from "@mui/material/styles";
import { Typography, useMediaQuery, useTheme } from "@mui/material";
import Skeleton from "@mui/material/Skeleton";

import AnimateWrapper from "@common/animations/AnimateWrapper";
import FloatingMenu from "@common/FloatingMenu";
import ShopLogo from "@common/ShopLogo";
import useShopNav from "@hooks/useShopNav";
import Categories from "./components/Categories";
import ProductCard from "./components/ProductCard";

import { isDark } from "@common/utils/colors";
import { filterProductsByCategory, filterProductsByTime } from "@common/utils/productListUtils";
import useShopContext from "@hooks/useShopContext";

const PREFIX = "ProductsContainer";

const classes = {
  animateRoot: `${PREFIX}-animateRoot`,
  container: `${PREFIX}-container`,
  placeholderText: `${PREFIX}-placeholderText`,
  shopLogoRoot: `${PREFIX}-shopLogoRoot`,
};

const Root = styled("div")(({ theme, layout }) => ({
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
  fontDisplay: "swap",
  backgroundColor: theme.palette.primary.main,
  justifyContent: "center",
  alignItems: "center",

  [`& .${classes.animateRoot}`]: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    backgroundColor: theme.palette.primary.main,
    flex: 1,
    maxWidth: 524,
    gap: theme.spacing(2),
    padding: theme.spacing(5, 0),
    [theme.breakpoints.down("sm")]: {
      width: "90vw",
      justifyContent: "flex-start",
    },
  },

  [`& .${classes.container}`]: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    flexWrap: "wrap",
    gap: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      display: "inline-grid",
      margin: "0 auto",
      gap: theme.spacing(2),
      gridTemplateColumns: layout === "column" ? "1fr" : "1fr 1fr",
      width: layout === "column" ? "70%" : "100%",
    },
  },

  [`& .${classes.placeholderText}`]: {
    color: isDark(theme.palette.primary.main) ? "#FFF" : "#000",
  },

  [`& .${classes.shopLogoRoot}`]: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
  },
}));

const ProductsContainer = () => {
  const { shop, loading, products } = useShopContext();
  const { renderSprite: catRenderSprite, selectedCategory, setCategory } = useShopNav();
  const theme = useTheme();
  const mobileView = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Root layout={shop?.layout}>
      <AnimateWrapper className={classes.animateRoot} key="products">
        {shop?.display_logo && (shop?.logo || shop?.vendor?.company_photo) ? (
          <div className={classes.shopLogoRoot}>
            <ShopLogo style={{ marginBottom: 16 }} />
          </div>
        ) : (
          <div />
        )}
        <Categories
          shop={shop}
          layout={shop?.layout}
          category={selectedCategory}
          setCategory={setCategory}
        />
        {!loading ? (
          products &&
          products.length > 0 &&
          filterProductsByCategory(products, selectedCategory).length > 0 ? (
            <div className={classes.container}>
              {filterProductsByTime(filterProductsByCategory(products, selectedCategory)).map(
                (product, index) => {
                  const renderSprite =
                    !mobileView || shop?.layout === "grid" ? index === 1 : index === 0;
                  return (
                    <ProductCard
                      key={index}
                      renderSprite={catRenderSprite && renderSprite}
                      product={product}
                      layout={shop?.layout}
                    />
                  );
                },
              )}
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr",
                placeItems: "center",
                padding: 40,
              }}
            >
              <Typography variant="subtitle1" className={classes.placeholderText}>
                No products found.
              </Typography>
            </div>
          )
        ) : (
          <div className={classes.container}>
            {[...Array(shop?.layout === "column" ? 3 : 4)].map((_, index) => (
              <div
                key={index}
                style={{ width: mobileView ? "100%" : "250px", padding: theme.spacing(1.5) }}
              >
                {/* Skeleton for Title */}
                <Skeleton variant="text" width="80%" sx={{ fontSize: "1rem", mb: 0.5 }} />
                {/* Skeleton for Price */}
                <Skeleton variant="text" width="40%" sx={{ fontSize: "0.875rem", mb: 1 }} />
                {/* Skeleton for Image Container (aspect ratio) */}
                <div style={{ width: "100%", paddingTop: "100%", position: "relative" }}>
                  {/* Skeleton for Image */}
                  <Skeleton
                    variant="rectangular"
                    sx={{ position: "absolute", top: 0, width: "100%", height: "100%" }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </AnimateWrapper>
      <FloatingMenu shop={shop} />
    </Root>
  );
};

export default ProductsContainer;
